import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {lobbyCasino, ricerca, sezioneAccesa} from '../../moduli/leggi';
import {useSkin} from '../../moduli/useSkin';
import {eOspite} from '../../utils/currentUser';
import {cambiaPreferito, descriviErrore, giochi as caricaGiochi, preferiti as leggiPreferiti} from '../../services/casinoApi';
import Caricamento from '../common/Caricamento';
import Nota from '../Vetrina/Nota';
import Copertina from './Copertina';
import Pagine, {usePagine, useRicordaScorrimento} from './Pagine';
import {percorsoSezione, stileColori} from './comune';
import './Casino.css';

/**
 * LA PAGINA DEI RISULTATI DELLA RICERCA (/cerca?q=roulette): solo i giochi
 * trovati, per nome o per studio, fra Casino' e Live casino accesi. Ci si
 * arriva dalla casella in testata (stile Nova) con Invio; in cima c'e' la
 * stessa casella, per correggere la ricerca senza tornare su (e sul
 * telefono, dove in testata non c'e' posto, e' l'unica).
 *
 * COSA DECIDE IL BACKOFFICE (modulo Ricerca giochi): titolo, frase quando
 * non trova niente, se cercare anche per studio e nel Live casino, la
 * misura delle copertine, i colori; quanti giochi per pagina, il modulo
 * Giochi per pagina. Preferiti e Prova seguono la lobby della sezione del
 * gioco: un gioco del Live casino si comporta come nel Live casino.
 * COSA DECIDE IL FORNITORE: quali giochi ci sono. Si cerca nello stesso
 * elenco della lobby (casino-service, coi giochi spenti per la skin gia'
 * tolti): la ricerca non trova niente che la lobby non mostrerebbe.
 */
export default function Ricerca({userService}) {
  useSkin();
  const {t} = useTranslation();
  const navigate = useNavigate();
  const [parametri, setParametri] = useSearchParams();
  const modulo = ricerca();
  const cercato = (parametri.get('q') || '').trim();
  const [scritto, setScritto] = useState(cercato);
  useEffect(() => { setScritto(cercato); }, [cercato]);
  const [cuori, setCuori] = useState(leggiPreferiti);

  /* Le sezioni in cui cercare: le lobby accese della skin, il Live casino solo se il modulo lo vuole. */
  const sezioni = ['casino'].concat(modulo.liveCasino ? ['liveCasino'] : []).filter((k) => sezioneAccesa(k));
  const chiaveSezioni = sezioni.join(',');
  const [stato, setStato] = useState({carica: true, errore: '', elenco: []});
  useEffect(() => {
    let vivo = true;
    const quali = chiaveSezioni ? chiaveSezioni.split(',') : [];
    setStato({carica: true, errore: '', elenco: []});
    Promise.allSettled(quali.map((k) => caricaGiochi(k).then((elenco) => elenco.map((g) => Object.assign({}, g, {sezione: k})))))
        .then((esiti) => {
          if (!vivo) return;
          const andati = esiti.filter((e) => e.status === 'fulfilled');
          /* Una sezione che non risponde non nasconde l'altra; se non risponde nessuna, si dice perche'. */
          if (quali.length && !andati.length) setStato({carica: false, errore: descriviErrore(esiti[0].reason), elenco: []});
          else setStato({carica: false, errore: '', elenco: andati.flatMap((e) => e.value)});
        });
    return () => { vivo = false; };
  }, [chiaveSezioni]);

  const testo = cercato.toLowerCase();
  const trovati = useMemo(() => (testo
    ? stato.elenco.filter((g) => g.nome.toLowerCase().includes(testo) || (modulo.studi && (g.fornitore || '').toLowerCase().includes(testo)))
    : []), [stato.elenco, testo, modulo.studi]);
  const pagine = usePagine(trovati, testo);
  /* Tornando da un gioco, i risultati ripartono dal punto in cui li si e' lasciati. */
  useRicordaScorrimento(!stato.carica);

  const cerca = (e) => {
    e.preventDefault();
    const q = scritto.trim();
    if (q) setParametri({q}); else setParametri({});
  };
  const apri = (g) => {
    if (eOspite()) {
      if (userService && userService.richiediAccesso) userService.richiediAccesso();
      return;
    }
    navigate(`${percorsoSezione(g.sezione)}/gioco/${encodeURIComponent(g.id)}`);
  };
  const prova = (g) => navigate(`${percorsoSezione(g.sezione)}/gioco/${encodeURIComponent(g.id)}?prova=1`);
  const lobby = {casino: lobbyCasino('casino'), liveCasino: lobbyCasino('liveCasino')};

  return (
    <div className={'cas-lobby cas-ricerca cas-' + modulo.copertine} style={stileColori(modulo.colori)}>
      <div className={'cas-intesta'}>
        <h1>{modulo.titolo || t('Risultati della ricerca')}</h1>
        <form className={'cas-filtri'} role={'search'} onSubmit={cerca}>
          <input type={'search'} className={'cas-cerca'} value={scritto} onChange={(e) => setScritto(e.target.value)}
            placeholder={modulo.segnaposto || t('Cerca giochi')} aria-label={modulo.segnaposto || t('Cerca giochi')}/>
          <button type={'submit'} className={'cas-cerca-vai'}>{t('Cerca')}</button>
        </form>
      </div>

      {!sezioni.length && <Nota tono={'spento'} giocatore={t('Su questo sito non ci sono giochi da cercare.')}>{t('Casinò e Live casino sono spenti per questa skin: non ci sono giochi da cercare.')}</Nota>}
      {sezioni.length > 0 && !cercato && <Nota>{t('Scrivi il nome di un gioco o di uno studio e premi Invio.')}</Nota>}
      {sezioni.length > 0 && cercato && stato.carica && <Caricamento forma={'righe'} righe={3} testo={t('Cerco i giochi')}/>}
      {cercato && stato.errore && (
        <Nota tono={'errore'}>
          {stato.errore}
        </Nota>
      )}
      {cercato && !stato.carica && !stato.errore && sezioni.length > 0 && (
        trovati.length
          ? (
            <>
              <p className={'cas-ricerca-conta'}>{t('{{count}} giochi per «{{testo}}»', {count: trovati.length, testo: cercato})}</p>
              <div className={'cas-griglia'}>
                {pagine.visibili.map((g) => (
                  <Copertina key={g.sezione + ':' + g.id} gioco={g} preferito={cuori.includes(g.id)}
                    conPreferiti={lobby[g.sezione].preferiti} conProva={lobby[g.sezione].prova}
                    onApri={apri} onProva={prova} onPreferito={(id) => setCuori(cambiaPreferito(id))}/>
                ))}
              </div>
              <Pagine {...pagine}/>
            </>
          )
          : <Nota>{modulo.vuoto || t('Nessun gioco trovato.')}</Nota>
      )}
    </div>
  );
}
